import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * When the browser restores a page from back/forward cache (common in
   * Incognito), custom elements stay mounted with a stale cart count and
   * empty cart-drawer markup. Re-sync badge + refresh drawer sections.
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    // Soft back / bfcache both leave stale drawer HTML; always re-sync.
    this.syncCartBubbleFromServer(true);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Ignore our own sync event — badge already updated in syncCartBubbleFromServer
    if (event.detail.data?.source === 'cart-icon-sync') return;

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Product form events pass the qty just added; other events pass the full cart total.
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    const displayCount = this.currentCartCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', displayCount > 0 && animate);
    this.classList.toggle('header-actions__cart-icon--has-cart', displayCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(displayCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Fetch live cart count from Shopify and update the badge.
   * @param {boolean} [force] - When true (bfcache restore), always overwrite and refresh drawer HTML.
   */
  syncCartBubbleFromServer = async (force = false) => {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      const itemCount = Number(cart.item_count) || 0;

      const countChanged = itemCount !== this.currentCartCount;
      if (!force && !countChanged) return;

      this.renderCartBubble(itemCount, false, false);

      // Cart drawer / cart page HTML can still be the empty bfcache snapshot —
      // ask cart-items-component to re-render its section from the server.
      if (force || countChanged) {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'cart-icon', {
            source: 'cart-icon-sync',
            itemCount,
          })
        );
      }
    } catch (_) {
      // Keep the current badge if the request fails
    }
  };

  /**
   * Checks if the cart count is correct (sessionStorage quick path + live verify).
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount !== null) {
      try {
        const { value, timestamp } = JSON.parse(sessionStorageCount);
        const count = parseInt(value, 10);
        const visibleCount = this.currentCartCount;

        if (
          Number.isFinite(count) &&
          count >= 0 &&
          count !== visibleCount &&
          Date.now() - timestamp < 30 * 60 * 1000
        ) {
          this.renderCartBubble(count, false, false);
        }
      } catch (_) {
        // ignore bad session data
      }
    }

    this.syncCartBubbleFromServer(false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
